import type React from "react";
import { useState, useEffect } from "react";
import { billingApi, type OrganizationBilling } from "../../services/billingApi";
import "./CreditBalance.css";

interface CreditBalanceProps {
  onClick?: () => void;
}

// Module-level cache for billing data
let billingCache: { data: OrganizationBilling | null; timestamp: number } = {
  data: null,
  timestamp: 0,
};
const BILLING_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const CreditBalance: React.FC<CreditBalanceProps> = ({ onClick }) => {
  const [billing, setBilling] = useState<OrganizationBilling | null>(billingCache.data);
  const [loading, setLoading] = useState(!billingCache.data);

  useEffect(() => {
    const isCacheValid = billingCache.data && Date.now() - billingCache.timestamp < BILLING_CACHE_TTL;
    if (!isCacheValid) {
      loadBilling();
    }
    // Refresh every 5 minutes
    const interval = setInterval(loadBilling, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadBilling = async () => {
    try {
      const dashboard = await billingApi.getDashboard();
      setBilling(dashboard.billing);
      billingCache = { data: dashboard.billing, timestamp: Date.now() };
    } catch (error) {
      console.error("Failed to load billing info:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !billing) {
    return (
      <div className="credit-balance-widget loading">
        <div className="credit-skeleton">
          <div className="skeleton-line short"></div>
          <div className="skeleton-line long"></div>
        </div>
      </div>
    );
  }

  const credits = billing.available_credits;
  const isLow = credits < 100;

  return (
    <div
      className={`credit-balance-widget ${isLow ? "low-balance" : ""}`}
      onClick={onClick}
      title="Click to view billing dashboard"
    >
      <div className="credit-icon">
        <svg
          width="24"
          height="24"
          viewBox="0 0 32 32"
          fill="none"
          style={{ filter: "drop-shadow(0 2px 4px rgba(234, 179, 8, 0.25))" }}
        >
          <defs>
            <linearGradient id="coinGradNav" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FDE047" />
              <stop offset="50%" stopColor="#EAB308" />
              <stop offset="100%" stopColor="#A16207" />
            </linearGradient>
            <linearGradient id="coinInnerNav" x1="1" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="#FEF08A" />
              <stop offset="100%" stopColor="#CA8A04" />
            </linearGradient>
          </defs>
          <circle cx="16" cy="16" r="14" fill="url(#coinGradNav)" />
          <circle cx="16" cy="16" r="10.5" fill="url(#coinInnerNav)" />
          <path d="M19 11.5 A 5.5 5.5 0 1 0 19 20.5" stroke="#FEF9C3" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          <line x1="16" y1="9" x2="16" y2="23" stroke="#FEF9C3" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </div>
      <div className="credit-info">
        <div className="credit-label">Credits</div>
        <div className="credit-amount">{credits.toLocaleString()}</div>
      </div>
      {isLow && <div className="low-badge">!</div>}
    </div>
  );
};
